import { toPillarApiError } from "shared/api/PillarApiError";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";

const PillarTableRowInlineDelete = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const { testidTable, handleInlineDelete, refetchData } =
    usePillarTableContext<
      RowGeneric,
      ExtraPropsGeneric,
      InlineEditFormGeneric
    >();
  const {
    rowIndex,
    inlineEditMode,
    setInlineEditMode,
    rowValue,
    setRowActionFailure,
  } = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  if (!refetchData || !handleInlineDelete || inlineEditMode === "edit") {
    return <></>;
  }
  if (inlineEditMode === "none") {
    return (
      <td
        data-testid={`${testidTable}-table-body-row-column-inlineDelete`}
        key={`row-${rowIndex}-column-inlineDelete`}
        className="whitespace-nowrap pillartable-table-cell-general "
      >
        <div className="flex gap-x-1 break-words overflow-visible whitespace-normal">
          <div className="">
            <PillarForm.Button
              className="flex items-center justify-center gap-x-0.5 button-small-destructive-filled"
              testid={"delete-button"}
              onClick={(event) => {
                setInlineEditMode("delete");
                event.stopPropagation();
              }}
            >
              Delete
            </PillarForm.Button>
          </div>
        </div>
      </td>
    );
  }
  if (inlineEditMode === "delete" || inlineEditMode === "deleting") {
    return (
      <td
        data-testid={`${testidTable}-table-body-row-column-inlineDelete`}
        key={`row-${rowIndex}-column-inlineDelete`}
        className="whitespace-nowrap pillartable-table-cell-general"
      >
        <PillarForm.Button
          className="button-small-neutral-filled mr-1 mb-1"
          testid="cancel-button"
          disabled={inlineEditMode === "deleting"}
          onClick={(event) => {
            setInlineEditMode("none");
            event.stopPropagation();
          }}
        >
          Cancel
        </PillarForm.Button>
        <PillarForm.Button
          className="button-small-destructive-filled"
          testid="confirm-delete-button"
          disabled={inlineEditMode === "deleting"}
          onClick={async (event) => {
            setInlineEditMode("deleting");
            event.stopPropagation();
            // `false` means nothing was deleted: put the row back to its
            // normal state so it is not stuck on disabled buttons.
            // `undefined` still counts as success for handlers returning void.
            // A throw is shown in the row in place of these buttons until
            // dismissed, so the handler does not have to catch it just to
            // tell the user why.
            let deleted = false;
            try {
              deleted =
                (await handleInlineDelete(rowValue, refetchData)) !== false;
              if (!deleted) {
                setInlineEditMode("none");
              }
            } catch (error) {
              console.error(error);
              setInlineEditMode("delete");
              setRowActionFailure({
                action: "delete",
                error: toPillarApiError(error),
              });
            }
            if (deleted) {
              await refetchData();
            }
          }}
        >
          Confirm Delete
        </PillarForm.Button>
      </td>
    );
  }
  return (
    <td
      data-testid={`${testidTable}-table-body-row-column-inlineDelete`}
      key={`row-${rowIndex}-column-inlineDelete`}
      className="whitespace-nowrap pillartable-table-cell-general"
    ></td>
  );
};

export default PillarTableRowInlineDelete;
